import { formatDate, formatDateOrDash, formatDateInput } from '@/lib/format/date';
import { formatContact, formatTextOrDash } from '@/lib/format/text';
import { formatNumber, formatPercentOrDash } from '@/lib/format/number';
import { buildQuantitiesByUnit, defaultUnitType } from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { isEscaBonusCapOption } from '@/config/declarationOptions';
import { formValue } from '@/lib/mappers/formValues';
import { buildFulfillmentFiltersInput, processFilterOptions } from '@/lib/mappers/filters';
import { allocationStatusLabel } from '../constants/allocationStatus';

// DTO ⇄ view model for GET /:module/pending-orders/fulfillments.

// Unlike the orders and shipments routes, this one sends the units as plain strings.
const buildUnitsByType = (dto) => ({
  [UNIT_TYPES.working]: {
    unit: dto.workingUnit,
    amounts: { certifiedQuantity: dto.certifiedQuantityWorking },
  },
  [UNIT_TYPES.declaration]: {
    unit: dto.declarationUnit,
    amounts: { certifiedQuantity: dto.certifiedQuantityDeclaration },
  },
});

// Order id, then shipment id(s), then the inbound declaration ref — whichever exist.
// TODO: the DTO carries one `shipmentNo`, so a multi-shipment allocation lists only the first.
const buildReference = (reference) =>
  formatTextOrDash(
    [reference?.orderNo, reference?.shipmentNo, reference?.incomingDeclarationRef]
      .filter(Boolean)
      .join(', '),
  );

// The server ORs FulfillmentSearch's keys, so one box searches all four.
const buildFulfillmentSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return {
    transactionId: text,
    declarationNo: text,
    material: { name: text },
    customerAccount: { companyname: text },
  };
};

// Screen state → `fulfillments` variables. The list has no sort: the server orders by createdAt desc.
export const buildFulfillmentsQuery = ({
  certificateType,
  page,
  pageSize,
  search,
  allocationPreference,
  materialId,
  customerAccountId,
  extraFilters,
} = {}) => ({
  certificateType,
  page,
  limit: pageSize,
  search: buildFulfillmentSearchInput(search),
  // TODO: `status` and `daysLeft` have no FulfillmentFilters field; the REST route dropped them too.
  filters: buildFulfillmentFiltersInput(
    { allocationPreference, materialId, customerAccountId },
    extraFilters,
  ),
});

export const processFulfillmentFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  customerOptions: processFilterOptions(result?.customers),
});

export const processAllocationsPage = (connection) => ({
  rows: (connection?.nodes ?? []).map(processAllocation),
  totalCount: connection?.pageInfo?.total ?? 0,
});

export const processAllocation = (dto) => {
  const unitsByType = buildUnitsByType(dto);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: dto.id,
    transactionId: formatTextOrDash(dto.transactionId),
    // The declaration this allocation generated; `associatedDeclarationNo` is the inbound one.
    declarationNo: formatTextOrDash(dto.declarationNo),
    hasDeclaration: Boolean(dto.declarationNo),
    // TODO: `internalTransfer` has no field on `Fulfillment` yet.
    internalTransfer: '-',
    feedstockCategory: formatTextOrDash(dto.feedstockCategory),
    materialName: formatTextOrDash(dto.materialName),
    facilityName: formatTextOrDash(dto.sentFrom),
    // The declaration save resolves its site type from this.
    siteId: dto.siteId ?? null,
    terminal: formatTextOrDash(dto.terminal),
    status: allocationStatusLabel(dto.approvalStatus) ?? formatTextOrDash(dto.approvalStatus),
    // The raw value too: the row's "..." menu differs per status.
    statusCode: dto.approvalStatus ?? null,
    canGenerateDeclaration: Boolean(dto.canGenerateDeclaration),
    canRevoke: Boolean(dto.canRevoke),
    sendTo: formatTextOrDash(dto.receivedAt),
    shippingTo: formatTextOrDash(dto.shippingTo ?? dto.shippingToSites),
    transactionDate: formatDateOrDash(dto.transactionDate),
    byUnit,
    unitType: defaultUnitType(byUnit),
    massBalancePeriod: formatTextOrDash(dto.massBalancePeriodLabel),
    reference: buildReference(dto.reference),
    associatedDeclarationNo: formatTextOrDash(dto.associatedDeclarationNo),
    transactedBy: formatContact(dto.transactedBy?.name, dto.transactedBy?.email),
    countryOfOrigin: formatTextOrDash(dto.countryOfOrigin),
    rawMaterialType: formatTextOrDash(dto.rawMaterialType),
    ghgEmissions: dto.ghgEmission == null ? '-' : formatNumber(dto.ghgEmission),
    ghgEmissionSavings: formatPercentOrDash(dto.ghgEmissionSavings),
    allocatedQuota: formatTextOrDash(dto.allocatedQuota),
    dateOfDispatch: formatDateOrDash(dto.dateOfDispatch),
    daysLeft: dto.daysLeft ?? null,
    // The Edit declaration form seeds off these: a control needs the raw value, not "-".
    rawMaterialTypeCode: dto.rawMaterialType ?? '',
    countryOfOriginCode: dto.countryOfOrigin ?? '',
    feedstockCategoryCode: dto.feedstockCategory ?? '',
    dateOfDispatchInput: formatDateInput(dto.dateOfDispatch),
    ghgEmissionsRaw: dto.ghgEmission ?? '',
    ghgEmissionSavingsRaw: dto.ghgEmissionSavings ?? '',
    declarationUnit: dto.declarationUnit ?? dto.unit ?? '',
    // The unformatted figures beside the formatted set — a shipment quantity caps against them.
    rawByUnit: {
      [UNIT_TYPES.working]: dto.certifiedQuantityWorking ?? null,
      [UNIT_TYPES.declaration]: dto.certifiedQuantityDeclaration ?? null,
    },
    // The server matches a shipment's unit by name, and `byUnit` holds the symbol.
    unitByType: {
      [UNIT_TYPES.working]: dto.workingUnit ?? null,
      [UNIT_TYPES.declaration]: dto.declarationUnit ?? null,
    },
    // Update shipment details posts under the order this allocation fulfils.
    orderId: dto.reference?.pendingOrderId ?? null,
    // Decides the declaration modal's CTA pair; the list needs it for the bulk action.
    requiredFieldsComplete: Boolean(dto.requiredFieldsComplete),
    // Download hands over the stored PDF, so a row needs the file, not just the number.
    declarationFileId: dto.fileId ?? null,
  };
};

// The detail page reads the same DTO; only the header adds fields the list has no column for.
export const processAllocationDetail = (dto) => {
  if (!dto) return null;
  return {
    ...processAllocation(dto),
    vesselName: formatTextOrDash(dto.vesselName),
  };
};

// One Update-shipment card → POST /:module/pending-orders/:orderId/shipments; the order supplies material, site and customer.
export const buildAllocationShipmentDto = (shipment, unitByType) => ({
  shipmentNo: [shipment.idPrefix, shipment.idNumber].filter(Boolean).join('-') || undefined,
  shipmentQuantity: Number(shipment.quantity),
  shipmentQuantityUnit: unitByType?.[shipment.unitType] || undefined,
  dispatchDate: shipment.dateOfDispatch,
  vesselName: shipment.vesselName || undefined,
});

const processAllocationAttachment = (attachment, index) => ({
  id: attachment.fileId,
  fileId: attachment.fileId,
  serialNo: index + 1,
  name: formatTextOrDash(attachment.fileName),
  lastUpdatedOn: formatTextOrDash(formatDate(attachment.uploadedAt, 'dd/MM/yyyy | HH:mm')),
});

export const processAllocationAttachments = (attachments) =>
  (attachments ?? []).map(processAllocationAttachment);

// The SD number is stored whole and edited as prefix + number.
const splitSdNumber = (value) => {
  const [prefix, ...rest] = String(value ?? '').split('-');
  return rest.length ? { idPrefix: prefix, idNumber: rest.join('-') } : { idNumber: prefix };
};

// `allocationDeclaration(id)` → the Edit declaration form's own names.
export const processAllocationDeclaration = (node) => {
  if (!node) return null;
  const details = node.declarationDetails ?? {};
  const material = node.materialsDetails ?? {};
  const emission = node.emissionDetails ?? {};

  return {
    ...splitSdNumber(details.sustainabilityDeclarationNumber),
    // Download hands over the stored file; null means the server holds none yet.
    declarationFileId: node.declarationFileId ?? null,
    dateOfIssuance: formatDateInput(details.dateOfIssuance),
    dateOfDispatch: formatDateInput(details.dateOfDispatch),

    supplierName: formValue(details.supplierName),
    supplierAddress: formValue(details.supplierAddress),
    supplierCertificateNumber: formValue(details.supplierCertificateNumber),
    supplierDispatchAddress: formValue(details.supplierAddressOfDispatch),
    recipientName: formValue(details.recipientName),
    recipientAddress: formValue(details.recipientAddress),
    contractNumber: formValue(details.recipientContractNumber),
    recipientReceiptAddress: formValue(details.recipientAddressOfReceipt),

    typeOfProduct: formValue(material.typeOfProduct),
    rawMaterialType: formValue(material.typeOfRawMaterial),
    additionalInformation: formValue(material.additionalInformation),
    countryOfOrigin: formValue(material.countryOfOrigin),
    totalCertifiedQuantity: formValue(node.allocation?.certifiedQuantity),
    unit: formValue(node.allocation?.unit),
    energyContent: formValue(node.energyContent),

    isccCompliantMaterial: formValue(material.isccCompliantMaterial),
    euRedCompliantMaterial: formValue(material.euRedCompliantMaterial),
    chainOfCustodyOption: formValue(material.chainOfCustodyOption),
    countryOfBiofuelProduction: formValue(material.countryOfBiofuelProduction),
    startDateOfBiofuelProduction: formatDateInput(material.startDateOfBiofuelProduction),
    startDateOfBioliquidUse: formatDateInput(material.startDateOfBioliquidUse),
    sustainabilityCriteriaCompliance: formValue(material.rawMaterialSustainabilityCriteria),
    intermediateCrop: formValue(material.biomassIntermediateCrop),
    lowIlucRisk: formValue(material.biomassIlucRiskFeedstocks),
    wasteOrResidue: formValue(material.rawMaterialWasteDefinition),
    wastePermitNumber: formValue(material.wasteAnimalByProductPermitNumber),
    supportReceived: formValue(material.supportForProduction),
    supportNatureAndScheme: formValue(material.supportNatureAndScheme),

    // The column keeps the red_2 name; the schema and the form both say RED III.
    defaultValueApplied: formValue(emission.ghgEmissionRed3),
    totalGhgEmissions: formValue(emission.totalGhgEmissions),
    eec: formValue(emission.ghgEec),
    el: formValue(emission.ghgEl),
    ep: formValue(emission.ghgEp),
    etd: formValue(emission.ghgEtd),
    eu: formValue(emission.ghgEu),
    esca: formValue(emission.ghgEsca),
    eccs: formValue(emission.ghgEccs),
    eccr: formValue(emission.ghgEccr),
    allocatedHeat: formValue(emission.allocatedHeat),
    allocatedElectricity: formValue(emission.allocatedElectricity),
    // Non-option values (OCR free text / legacy labels) load as empty.
    escaBonusCap: isEscaBonusCapOption(emission.escaBonusCap)
      ? formValue(emission.escaBonusCap)
      : '',
    nuts2Area: formValue(emission.nuts2Area),

    savingTransport: formValue(emission.biofuelsForTransport),
    savingElectricity: formValue(emission.bioliquidsBiomassFuelsElectricity),
    savingElectricityOutermost: formValue(emission.biomassFuelsElectricityOutermostRegions),
    savingUsefulHeat: formValue(emission.bioliquidsBiomassFuelsUsefulHeat),
    savingHeatSubstitution: formValue(emission.biomassFuelsUsefulHeatCoalSubstitution),

    foodSecurityStandard: formValue(emission.fssFoodSecurityStandard),
    euDeforestationRegulation: formValue(emission.eudrDeforestationRegulation),
    nonGmoFoodFeed: formValue(emission.nonGmoFoodFeed),
    nonGmoTechnicalMarkets: formValue(emission.nonGmoTechnicalMarkets),

    shippingDistanceKm: formValue(emission.shippingDistanceKm),
    trainDistanceKm: formValue(emission.trainDistanceKm),
    roadDistanceKm: formValue(emission.roadDistanceKm),
    disaggregatedDefaultValueForOil: formValue(emission.disaggregatedDefaultValueForOil),
    disaggregatedDefaultValueForSoil: formValue(emission.disaggregatedDefaultValueForSoil),
    elBonusObtained: formValue(emission.elBonusObtained),
  };
};
